import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";

const TICK = { fontSize: 11, fill: "var(--mm-text-secondary)" };
const SCORE = [0, 100];
const MOOD = [0, 10];
const SIZE = { width: 320, height: 224 };
const RADIUS = [6, 6, 0, 0];

const pattern = (c) => {
  if (c.length < 2) return "Take another check in two weeks to start seeing a pattern.";
  const [a, b] = c.slice(-2), d = b.score - a.score;
  const s = d === 0 ? "Your wellbeing held steady since your last check" : `Your wellbeing ${d > 0 ? "rose" : "dipped"} ${Math.abs(d)} points since your last check`;
  if (a.avg_mood == null || b.avg_mood == null) return `${s}.`;
  const m = +(b.avg_mood - a.avg_mood).toFixed(1);
  if (Math.abs(m) < 0.5) return `${s}, while your mood stayed about the same.`;
  return `${s}, and your average mood ${m > 0 ? "rose" : "fell"} ${Math.abs(m)} too${(m > 0) === (d > 0) ? ". They're moving together." : "."}`;
};

export const WellbeingInsights = () => {
  const [checks, setChecks] = useState(null);
  useEffect(() => { api.get("/wellbeing-check/insights").then((r) => setChecks(r.data.checks)).catch((e) => { logError("Wellbeing insights failed:", e); setChecks([]); }); }, []);
  if (!checks) return null;
  const data = checks.map((c) => ({ day: new Date(c.at).toLocaleDateString(undefined, { month: "short", day: "numeric" }), wellbeing: c.score, mood: c.avg_mood }));
  return (
    <div className="mm-card p-5" data-testid="insights-wellbeing">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-semibold">Wellbeing & mood</h3>
        <Link to="/wellbeing" className="text-sm font-semibold text-mm-primary" data-testid="insights-wellbeing-take">{checks.length ? "Take a new check" : "Take your first check"} →</Link>
      </div>
      {checks.length ? (
        <>
          <p className="text-sm mt-1" data-testid="insights-wellbeing-pattern">{pattern(checks)}</p>
          <div className="h-60 mt-3">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} initialDimension={SIZE}>
              <ComposedChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--mm-border)" />
                <XAxis dataKey="day" tick={TICK} />
                <YAxis yAxisId="w" domain={SCORE} tick={TICK} width={30} />
                <YAxis yAxisId="m" orientation="right" domain={MOOD} tick={TICK} width={24} />
                <Tooltip />
                <Bar yAxisId="m" dataKey="mood" name="Avg mood (2 weeks before)" fill="var(--mm-secondary)" opacity={0.45} radius={RADIUS} />
                <Line yAxisId="w" dataKey="wellbeing" name="Wellbeing score" stroke="var(--mm-primary)" strokeWidth={3} dot={{ r: 4 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <p className="text-xs text-mm-muted mt-2">Line: wellbeing score (0–100, left). Bars: your average mood in the 2 weeks before each check (0–10, right).</p>
        </>
      ) : <p className="text-sm text-mm-muted mt-2" data-testid="insights-wellbeing-empty">A 2-minute check every couple of weeks shows how your overall wellbeing moves alongside your daily mood.</p>}
    </div>
  );
};
